import {
  ActionReducerMapBuilder,
  createAction,
  EntityState,
  EntityId,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { LocalStorage } from '@vk-hr-tek/core/local-storage';

import { ThunkExtra } from '@app/store';
import { GetSideMenuResponse, SideMenuService } from '@app/gen/side_menu';
import {
  ContentSearchRequest,
  ContentSearchResponse,
  ContentSearchGroupsResponse,
  SearchService,
} from '@app/gen/search';

import { LayoutState } from '../layout.state';
import { LayoutService } from '../../services';
import { SIDEBAR_STATE_LOCAL_STORAGE_KEY } from '../../constants';

export const setServiceUnavailable = createAction<boolean>(
  'layout/setServiceUnavailable',
);

const setSideMenu = createAction<GetSideMenuResponse>('layout/setSideMenu');
export const setSidebarState = createAsyncThunk<void, boolean, ThunkExtra>(
  'layout/setSidebarState',
  async (isOpen, { rejectWithValue, extra: { inject } }) => {
    try {
      const localStorageService = inject<LocalStorage>(LocalStorage);

      localStorageService.setItem(
        SIDEBAR_STATE_LOCAL_STORAGE_KEY,
        isOpen.toString(),
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

/** @knipignore */
export const setIsLogoLoaded = createAction<boolean>('layout/setIsLogoLoaded');

export const loadCandidateLogo = createAsyncThunk<
  void,
  { id: string | null },
  ThunkExtra
>(
  'layout/loadCandidateLogo',
  async (dto, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const layoutService = inject(LayoutService);

      const { id } = dto;

      if (id === null) {
        dispatch(setIsLogoLoaded(false));

        return;
      }

      const res = await layoutService.getCandidateLogo(id);

      if (res) {
        dispatch(setIsLogoLoaded(true));
      }
    } catch (err) {
      dispatch(setIsLogoLoaded(false));

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const search = createAsyncThunk<
  ContentSearchResponse,
  ContentSearchRequest,
  ThunkExtra
>('layout/search', async (params, { rejectWithValue, extra: { inject } }) => {
  try {
    const searchService = inject(SearchService);
    const result = await searchService.contentSearch({
      body: params,
    });

    return result;
  } catch (err) {
    return rejectWithValue(classToPlain(err) as AppError);
  }
});

export const getSideMenu = createAsyncThunk<
  GetSideMenuResponse,
  undefined,
  ThunkExtra
>('layout/getSideMenu', async (dto, { rejectWithValue, extra: { inject } }) => {
  try {
    const sideMenuService = inject(SideMenuService);
    const result = await sideMenuService.getSideMenu();

    return result;
  } catch (err) {
    return rejectWithValue(classToPlain(err) as AppError);
  }
});

export const resetState = createAction('layout/resetState');

export const getContentSearchGroups = createAsyncThunk<
  ContentSearchGroupsResponse,
  undefined,
  ThunkExtra
>(
  'layout/getContentSearchGroups',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const searchService = inject(SearchService);

      const result = await searchService.getContentSearchGroups();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const layoutReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<
      {
        id: EntityId;
      },
      EntityId
    > &
      LayoutState
  >,
) => {
  builder.addCase(setServiceUnavailable, (state, action) => {
    state.isServiceUnavailable = action.payload;
  });

  builder.addCase(setSidebarState.pending, (state, { meta }) => {
    state.sidebarState = meta.arg;
  });
  builder.addCase(setSidebarState.rejected, (state, { meta }) => {
    state.sidebarState = !meta.arg;
  });
  builder.addCase(setSideMenu, (state, action) => {
    state.sideMenuState = action.payload;
  });

  builder.addCase(setIsLogoLoaded, (state, action) => {
    state.isLogoLoaded = action.payload;
  });

  builder.addCase(loadCandidateLogo.pending, (state) => {
    state.logoLoadingStatus = 'loading';
  });
  builder.addCase(loadCandidateLogo.fulfilled, (state) => {
    state.logoLoadingStatus = 'complete';
  });
  builder.addCase(loadCandidateLogo.rejected, (state, { payload, error }) => {
    state.logoLoadingStatus = 'failed';
    state.logoLoadingError =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(search.pending, (state) => {
    state.search.status = 'loading';
  });
  builder.addCase(search.fulfilled, (state, { payload, meta }) => {
    state.search.status = 'complete';
    state.search.group = meta.arg?.group;
    state.search.items = payload;
  });
  builder.addCase(search.rejected, (state, { payload, error }) => {
    state.search.status = 'failed';
    state.search.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getSideMenu.pending, (state) => {
    state.sideMenuStatus = 'loading';
    state.sideMenuState = null;
  });
  builder.addCase(getSideMenu.fulfilled, (state, { payload }) => {
    state.sideMenuStatus = 'complete';
    state.sideMenuState = payload;
  });
  builder.addCase(getSideMenu.rejected, (state, { payload, error }) => {
    state.sideMenuStatus = 'failed';
    state.sideMenuState = null;
    state.sideMenuError =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getContentSearchGroups.pending, (state) => {
    state.groups.status = 'loading';
    state.groups.items = null;
  });
  builder.addCase(getContentSearchGroups.fulfilled, (state, { payload }) => {
    state.groups.status = 'complete';
    state.groups.items = payload.search_groups;
  });
  builder.addCase(
    getContentSearchGroups.rejected,
    (state, { payload, error }) => {
      state.groups.status = 'failed';
      state.groups.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
